import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { SourceName, StorageKeys } from '@core/enums';
import { ITokens, IUrlInfo } from '@core/interface';
import { Observable, of, skip } from 'rxjs';
import { SessionService } from '../session/session.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { GetCountryService } from '@core/services';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(ActivatedRoute);
  private sessionService = inject(SessionService);
  private getCountryService = inject(GetCountryService);

  #tokens!: ITokens;
  #urlInfo!: IUrlInfo;

  #parseUrl(url: string): { [key: string]: string } | IUrlInfo {
    const queryParams: { [key: string]: string } = {};
    const urlParts = url.split('?');

    if (urlParts.length === 1) {
      return {
        cat: this.sessionService.getItem(StorageKeys.CAT) || this.sessionService.getItem(StorageKeys.AUTH_TOKEN),
        country: this.sessionService.getItem(StorageKeys.COUNTRY),
        source: this.sessionService.getItem(StorageKeys.SOURCE) || SourceName.web,
        vin: this.sessionService.getItem(StorageKeys.VIN_ACTIVE_DASHBOARD),
        scheduleId: this.sessionService.getItem(StorageKeys.SCHEDULE_ID),
      }
    }

    const queryString = urlParts[1];
    const pairs = queryString.split('&');

    pairs.forEach(pair => {
      const [key, value] = pair.split('=');
      if (key && value) {
        queryParams[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });

    return queryParams;
  }

  setParameters(): Observable<Params> {
    const url = this.router.snapshot.fragment ?? '';
    const queryParamsFromFragment = this.#parseUrl(url);
    return of(queryParamsFromFragment);
  }

  getParametersFromPath(): Observable<Params> {
    return this.router.queryParams.pipe(skip(1))
  }

  set tokens(tokens: ITokens) {
    this.sessionService.setItem(StorageKeys.CAT, tokens.cat);
    this.#tokens = tokens;
  }

  get tokens(): ITokens {
    const cat = this.sessionService.getItem(StorageKeys.CAT);

    this.#tokens = {
      ...this.#tokens,
      cat: cat !== 'null' ? cat : this.sessionService.getItem(StorageKeys.CAT),
    }
    return this.#tokens;
  }

  set urlInfo(urlInfo: IUrlInfo) {
    this.sessionService.setItem(StorageKeys.COUNTRY, urlInfo.country);
    this.sessionService.setItem(StorageKeys.SOURCE, urlInfo.source);
    this.sessionService.setItem(StorageKeys.VIN_ACTIVE_DASHBOARD, urlInfo.vin);
    this.#urlInfo = urlInfo;
  }

  get urlInfo(): IUrlInfo {
    this.#urlInfo = {
      ...this.#urlInfo,
      country: this.sessionService.getItem(StorageKeys.COUNTRY),
      source: this.sessionService.getItem(StorageKeys.SOURCE),
      vin: this.sessionService.getItem(StorageKeys.VIN_ACTIVE_DASHBOARD),
    }

    return this.#urlInfo;
  }

  #getUserGuidByAuthToken() {
    const jwt = new JwtHelperService();
    if (!this.#tokens?.cat) {
      return '';
    }
    return jwt.decodeToken(this.#tokens.cat).sub.toUpperCase() as string;
  }

  shouldClear(country: string, vin: string, dealerId: string): boolean {
    const oldCountry = this.getCountryService.getCountry();
    const oldDealerId = ('@Ford-dealerbyFordPass' in localStorage) ? JSON.parse(localStorage.getItem('@Ford-dealerbyFordPass') as string)?.dealerId : '';
    const oldVin = ('@Ford-vehicleModel' in localStorage) ? JSON.parse(localStorage.getItem('@Ford-vehicleModel') as string)?.chassis : '';
    const oldUserUguid = ('@Ford-userProfile' in localStorage) ? JSON.parse(localStorage.getItem('@Ford-userProfile') as string)?.userGuid : '';
    const userGuid = this.#getUserGuidByAuthToken();
    const shouldClear = (
      userGuid !== oldUserUguid ||
      country !== oldCountry ||
      vin !== oldVin ||
      Number(oldDealerId) !== Number(dealerId)
    );
    return shouldClear;
  }
}
